import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TimesheetActions } from './timesheet.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TimesheetService } from '@core/services/timesheet.service';
import { HelperService } from '@core/helper/helper.service';

@Injectable()
export class TimesheetEffects {
  private actions$ = inject(Actions);
  private timesheetService = inject(TimesheetService);
  private translateService = inject(TranslateService);
  helperService = inject(HelperService);
  getTimesheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.loadTimesheets),
      mergeMap((data) =>
        this.timesheetService.getTimesheets(data).pipe(
          map((timesheets) =>
            timesheets.success
              ? TimesheetActions.loadTimesheetsSuccess(timesheets)
              : TimesheetActions.loadTimesheetsFailure(timesheets)
          ),
          catchError((error) =>
            of(
              TimesheetActions.loadTimesheetsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.addEditTimesheet),
      mergeMap((data) =>
        this.timesheetService.addTimesheet(data.timesheet).pipe(
          map((timesheet) => {
            if (timesheet.success) {
              return data.timesheet.id
                ? TimesheetActions.updateTimesheetSuccess(timesheet)
                : TimesheetActions.addTimesheetSuccess(timesheet);
            } else return TimesheetActions.addTimesheetFailure(timesheet);
          }),
          catchError((error) =>
            of(
              TimesheetActions.addTimesheetFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  deleteTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.deleteTimesheet),
      mergeMap((data) =>
        this.timesheetService.deleteTimesheet(data.id).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.deleteTimesheetSuccess({
                  ...response,
                  id: data.id,
                })
              : TimesheetActions.deleteTimesheetFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.deleteTimesheetFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  approveTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.approveTimesheets),
      mergeMap((data) =>
        this.timesheetService.approveTimesheet(data).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.approveTimesheetsSuccess(response)
              : TimesheetActions.approveTimesheetsFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.approveTimesheetsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addAuditNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.addAuditNote),
      mergeMap((data) =>
        this.timesheetService.addAuditNote(data).pipe(
          map((timesheet) => {
            if (timesheet.success) {
              return TimesheetActions.addAuditSuccess(timesheet);
            } else return TimesheetActions.addAuditFailure(timesheet);
          }),
          catchError((error) =>
            of(
              TimesheetActions.addAuditFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getAuditNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.loadAuditNotes),
      mergeMap(({ date, user_id }) =>
        this.timesheetService.getAuditNotes({ date, user_id }).pipe(
          map((notes) =>
            notes.success
              ? TimesheetActions.loadAuditNotesSuccess(notes)
              : TimesheetActions.loadAuditNotesFailure(notes)
          ),
          catchError((error) =>
            of(
              TimesheetActions.loadAuditNotesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  reopenTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.reopenTimesheets),
      mergeMap((data) =>
        this.timesheetService.reOpenTimesheet(data).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.reopenTimesheetsSuccess(response)
              : TimesheetActions.reopenTimesheetsFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.reopenTimesheetsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getTimesheetReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.loadTimesheetReport),
      mergeMap((data) =>
        this.timesheetService.getTimesheetReport(data).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.loadTimesheetReportSuccess(response)
              : TimesheetActions.loadTimesheetReportFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.loadTimesheetReportFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  unsubmitTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.unSubmitTimesheet),
      mergeMap((data) =>
        this.timesheetService.unsubmitTimesheet(data).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.unSubmitTimesheetSuccess(response)
              : TimesheetActions.unSubmitTimesheetFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.unSubmitTimesheetFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  changeCM$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.changeCM),
      mergeMap((data) =>
        this.timesheetService.chnageCM(data).pipe(
          map((timesheets) =>
            timesheets.success
              ? TimesheetActions.changeCMSuccess(timesheets)
              : TimesheetActions.changeCMFailure(timesheets)
          ),
          catchError((error) =>
            of(
              TimesheetActions.changeCMFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  storeManualOvertime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.storeManualOvertime),
      mergeMap(({ data }) =>
        this.timesheetService.storeOvertimeManual(data).pipe(
          map((timesheet) => {
            if (timesheet.success) {
              return TimesheetActions.storeManualOvertimeSuccess(timesheet);
            } else
              return TimesheetActions.storeManualOvertimeFailure(timesheet);
          }),
          catchError((error) =>
            of(
              TimesheetActions.storeManualOvertimeFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  adminDirectApprove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.adminDirectApprove),
      mergeMap(({ ids }) =>
        this.timesheetService.adminDirectApprove(ids).pipe(
          map((response) =>
            response.success
              ? TimesheetActions.adminDirectApproveSuccess(response)
              : TimesheetActions.adminDirectApproveFailure(response)
          ),
          catchError((error) =>
            of(
              TimesheetActions.adminDirectApproveFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TimesheetActions.exportTimesheetReportExcel),
      mergeMap((payload) =>
        this.timesheetService
          .getTimesheetReport({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
          })
          .pipe(
            map((data) =>
              data.success
                ? TimesheetActions.exportTimesheetReportExcelSuccess({
                    ...data,
                    ...payload,
                  })
                : TimesheetActions.exportTimesheetReportExcelFailure(data)
            ),
            catchError((error) =>
              of(
                TimesheetActions.exportTimesheetReportExcelFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TimesheetActions.exportTimesheetReportExcelSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.exportExcelTimesheetReport(
              data.data,
              data.start_date,
              data.end_date,
              data.user?.label ?? 'All'
            );
        })
      ),
    { dispatch: false }
  );
}
